import { GraphStorage } from "../storage/GraphStorage";
import { generateRandomId, } from "../view/utils/utilityFunctions";
export class GraphDataModel {
    constructor() {
        this.storage = null;
    }
    getEntitiesInNode(node) {
        if (!node || !node.selectedFieldsByCategory) {
            return [];
        }
        return Object.values(node.selectedFieldsByCategory).flat();
    }
    getUnusedSelectedEntitiesSetExcludingCurrent(flowState, nodeIndex, selectedEntitiesSet) {
        const currentNode = flowState[nodeIndex];
        const entitiesToExclude = this.getEntitiesInNode(currentNode);
        const selectedEntitiesSetExcludingCurrent = new Set(selectedEntitiesSet);
        entitiesToExclude.forEach((id) => selectedEntitiesSetExcludingCurrent.delete(id));
        return selectedEntitiesSetExcludingCurrent;
    }
    setEntitiesAndEdgesWithIndexes(newEntities, newEdges) {
        this.storage = new GraphStorage(newEntities, newEdges, {
            bidirectional: true,
        });
    }
    addNode(edgeNodeIndex, _selectedFieldIds, oldFlow) {
        const newNodeId = generateRandomId();
        const newNodeData = {
            id: newNodeId,
            selectedFieldsByCategory: undefined,
        };
        const newFlow = [
            ...oldFlow.slice(0, edgeNodeIndex + 1),
            newNodeData,
            ...oldFlow.slice(edgeNodeIndex + 1),
        ];
        return newFlow;
    }
    editNode(newNode, oldFlow) {
        const targetId = newNode.id;
        const newFlow = oldFlow.map((node) => {
            if (node.id === targetId) {
                return newNode;
            }
            return node;
        });
        return newFlow;
    }
    deleteNode(nodeIdToDelete, oldFlow) {
        const indexToDelete = oldFlow.findIndex((node) => node.id === nodeIdToDelete);
        if (indexToDelete === -1) {
            return oldFlow;
        }
        if (indexToDelete === 0) {
            return [];
        }
        const newFlow = oldFlow.slice(0, indexToDelete);
        return newFlow;
    }
    buildSelectedEntitiesSet(flowState) {
        const selectedEntitiesSet = new Set();
        for (const node of flowState) {
            const entitiesInNode = this.getEntitiesInNode(node);
            entitiesInNode.forEach((entity) => selectedEntitiesSet.add(entity));
        }
        return selectedEntitiesSet;
    }
    getEntityById(id) {
        if (!id || !this.storage)
            return undefined;
        return this.storage.getEntityById(id);
    }
    getCategoryKeys() {
        var _a;
        return ((_a = this.storage) === null || _a === void 0 ? void 0 : _a.getAllCategories().map((category) => category)) || [];
    }
    getPrecedingSourceFieldId(precedingNode) {
        var _a, _b;
        if (!precedingNode) {
            return undefined;
        }
        const precedingSelectedFieldId = (_b = (_a = Object.values((precedingNode === null || precedingNode === void 0 ? void 0 : precedingNode.selectedFieldsByCategory) || {})) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b[0];
        return precedingSelectedFieldId;
    }
    getLegalUnusedCategoriesForNewNode(flowState, nodeIndex, selectedEntitiesSet) {
        var _a;
        if (nodeIndex === 0) {
            return this.getCategoryKeys() || [];
        }
        const precedingNode = flowState[nodeIndex - 1];
        if (!precedingNode) {
            return [];
        }
        const precedingSelectedFieldId = this.getPrecedingSourceFieldId(precedingNode);
        if (!precedingSelectedFieldId) {
            return [];
        }
        const selectedEntitiesExcludingCurrent = this.getUnusedSelectedEntitiesSetExcludingCurrent(flowState, nodeIndex, selectedEntitiesSet);
        const legalNextCategoryMap = (_a = this.storage) === null || _a === void 0 ? void 0 : _a.getLegalEdgesGroupedByCategory(precedingSelectedFieldId);
        if (!legalNextCategoryMap) {
            return [];
        }
        const legalCategories = [];
        for (const [category, legalEntityIds] of legalNextCategoryMap.entries()) {
            const hasUnusedOption = legalEntityIds.some((entityId) => !selectedEntitiesExcludingCurrent.has(entityId));
            if (hasUnusedOption) {
                legalCategories.push(category);
            }
        }
        return legalCategories;
    }
    getLegalEntityOptionsByCategory(precedingFieldId, selectedCategory, flowState, nodeIndex, selectedEntitiesSet, getEntityLabelCallback) {
        var _a;
        if (!this.storage || !flowState) {
            console.error("Storage or flowState not initialized.");
            return [];
        }
        const legalNextCategoryMap = this.storage.getLegalEdgesGroupedByCategory(precedingFieldId);
        const legalEntityIds = (_a = legalNextCategoryMap.get(selectedCategory)) !== null && _a !== void 0 ? _a : [];
        if (legalEntityIds.length === 0) {
            return [];
        }
        const selectedEntitiesExcludingCurrent = this.getUnusedSelectedEntitiesSetExcludingCurrent(flowState, nodeIndex, selectedEntitiesSet);
        const filteredEntities = [];
        for (const id of legalEntityIds) {
            const entity = this.getEntityById(id);
            if (entity && !(selectedEntitiesExcludingCurrent === null || selectedEntitiesExcludingCurrent === void 0 ? void 0 : selectedEntitiesExcludingCurrent.has(entity.id))) {
                filteredEntities.push({
                    id: entity.id,
                    label: getEntityLabelCallback(entity.id),
                    category: entity.category,
                });
            }
        }
        return filteredEntities;
    }
    getRawEntitiesByCategory(category) {
        var _a;
        if (!category)
            return [];
        return ((_a = this.storage) === null || _a === void 0 ? void 0 : _a.getEntitiesByCategory(category)) || [];
    }
    hasLegalUnusedNextEdges(sourceId, flowState, nodeIndex, selectedEntitiesSet) {
        if (!this.storage || !flowState) {
            return false;
        }
        const legalTargetIds = this.storage.getLegalEdges(sourceId); // Get all connectable IDs
        if (legalTargetIds.length === 0) {
            return false;
        }
        const selectedEntitiesExcludingCurrent = this.getUnusedSelectedEntitiesSetExcludingCurrent(flowState, nodeIndex, selectedEntitiesSet);
        return legalTargetIds.some((targetId) => {
            const entity = this.getEntityById(targetId);
            return !!entity && !(selectedEntitiesExcludingCurrent === null || selectedEntitiesExcludingCurrent === void 0 ? void 0 : selectedEntitiesExcludingCurrent.has(targetId));
        });
    }
}
